<template>
  <div class="page-history">
    <LoadingSpinner v-if="loading" />
    <div v-else-if="!pageHistoryEvents.length">There is no history available for this page.</div>
    <div v-else-if="pageHistoryEvents.length" class="page-history-list">
      <historical-print-renderer v-slot="{ print }" :module-id="moduleId" :page-path="pagePath">
        <v-timeline
          density="compact"
          size="x-small"
          align="start"
          truncate-line="start"
          class="page-history-timeline"
        >
          <v-timeline-item
            v-for="(event, index) in pageHistoryEvents"
            :key="event.id"
            class="mb-4"
            :dot-color="index === 0 ? 'success' : 'grey'"
          >
            <div
              :class="['page-history-entry', index === 0 ? 'current-version' : 'clickable']"
              @click="index !== 0 && print(event)"
            >
              <div class="page-history-date-row">
                <span class="page-history-date">
                  {{ convertDateFormat(event.date) }}
                  <span v-if="index === 0" class="current-version-label">(Current version)</span>
                </span>
                <span
                  v-if="index !== 0 && canRestorePage"
                  :id="`restoreVersionBtn-${index}`"
                  class="restore-link"
                  @click.stop="restoreVersion(event)"
                >
                  Restore
                </span>
              </div>

              <hx-tooltip
                v-if="truncatedEvents[event.id]"
                v-slot="tooltipProps"
                :text="event.publishSummary || 'Module Published'"
                location="top"
              >
                <span
                  :ref="`summary-${event.id}`"
                  v-bind="tooltipProps"
                  class="page-history-summary"
                >
                  {{ event.publishSummary || 'Module Published' }}
                </span>
              </hx-tooltip>
              <span v-else :ref="`summary-${event.id}`" class="page-history-summary">
                {{ event.publishSummary || 'Module Published' }}
              </span>

              <span
                v-if="index !== 0"
                :id="`compareVersionBtn-${index}`"
                class="compare-link"
                @click.stop="compareVersion(event)"
              >
                Compare to current version
                <v-icon size="14" class="compare-icon">mdi-compare</v-icon>
              </span>
            </div>
          </v-timeline-item>
        </v-timeline>
      </historical-print-renderer>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment-timezone';
import * as types from '@/store/mutationTypes';
import LoadingSpinner from '@/components/core/LoadingSpinner';
import HistoricalPrintRenderer from '@/components/content/HistoricalPrintRenderer';

export default {
  name: 'PageHistory',

  components: {
    LoadingSpinner,
    'historical-print-renderer': HistoricalPrintRenderer
  },

  inject: {
    onCompareVersion: { default: () => () => {} },
    onRestoreVersion: { default: () => () => {} }
  },

  props: {
    canRestorePage: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      truncatedEvents: {}
    };
  },

  computed: {
    ...mapState({
      pageHistoryEvents: (state) => state.data.pageHistory
    }),

    moduleId() {
      const { params } = this.$route;
      const rawModuleId = params?.moduleId;
      const moduleId = parseInt(rawModuleId, 10);
      return moduleId;
    },

    pagePath() {
      const { params } = this.$route;
      const pagePath = params?.pagePath;
      return pagePath;
    }
  },

  watch: {
    $route() {
      this.getPageHistory();
    },
    pageHistoryEvents() {
      this.checkAllTruncations();
    }
  },

  async mounted() {
    this.getPageHistory();
  },

  methods: {
    async getPageHistory() {
      if (this.loading) return;
      try {
        this.loading = true;
        const details = {
          moduleId: this.moduleId,
          pagePath: this.pagePath
        };
        if (!details.moduleId || !details.pagePath) {
          return;
        }
        this.$store.commit(types.DATA_CLEAR_PAGE_HISTORY);
        await this.$store.dispatch('data/getPageHistory', details);
      } catch (err) {
        this.$store.commit(types.DATA_CLEAR_PAGE_HISTORY);
      } finally {
        this.loading = false;
      }
    },
    compareVersion(event) {
      this.onCompareVersion(event);
    },

    restoreVersion(event) {
      this.onRestoreVersion(event);
    },

    checkAllTruncations() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const result = {};
          this.pageHistoryEvents.forEach((event) => {
            const el = this.$refs[`summary-${event.id}`];
            const element = Array.isArray(el) ? el[0] : el;
            if (element) {
              result[event.id] = element.scrollHeight > element.clientHeight;
            }
          });
          this.truncatedEvents = result;
        });
      });
    },

    convertDateFormat(date) {
      return moment
        .tz(date, 'Australia/Sydney')
        .format('DD/MM/YYYY HH:mm:ss')
        .replace(/:[^:]*$/, '');
    }
  }
};
</script>

<style scoped lang="scss">
.page-history {
  display: flex;
  flex-direction: column;
  text-align: left;
  white-space: normal;
  overflow-x: hidden;
}

.page-history-list {
  overflow-x: hidden;
}

.page-history-timeline {
  padding-top: 0;
  padding-bottom: 12px;
}

.page-history-timeline :deep(.v-timeline-item__body) {
  min-width: 0;
  width: 100%;
}

.page-history-entry {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
  position: relative;
  width: 100%;
  padding-right: 10px;
}

.page-history-entry.clickable:hover {
  cursor: pointer;
}

.page-history-entry.clickable:hover:not(:has(.compare-link:hover, .restore-link:hover))
  .page-history-date {
  text-decoration: underline;
}

.page-history-date-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.page-history-date {
  color: #2f4051;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
}

.current-version-label {
  font-weight: 400;
  font-size: 13px;
}

.restore-link {
  color: rgb(var(--v-theme-primary));
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
}

.page-history-summary {
  color: #2f4051;
  font-size: 13px;
  font-weight: 400;
  white-space: pre-line;
  word-break: break-word;
  text-align: left;
  overflow: hidden;

  /* line-clamp to 10 lines */
  display: -webkit-box;
  -webkit-line-clamp: 10;
  line-clamp: 10;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  line-height: 1.5;
  max-height: 15em; /* 10 lines × 1.5 line-height = 15em */
}

.compare-link {
  color: rgb(var(--v-theme-primary));
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;

  &:hover {
    text-decoration: underline;
  }
}

.compare-icon {
  color: rgb(var(--v-theme-primary));
}
</style>
